import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { IconMastercard } from "@ui/icons/IconMastercard";
import Input from "@ui/input/Input";
import { Select } from "@ui/select/Select";
import { IconVisa } from "@ui/icons/IconVisa";
import { Check, ChevronRight, Copy, Info, Search, TriangleAlert, Wallet, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import styled, { css } from "styled-components";
import { IconApple } from "src/assets/terminal/icon_apple";
import { IconBank } from "src/assets/terminal/icon_bank";
import { IconCard } from "src/assets/terminal/icon_card";
import { IconGoogle } from "src/assets/terminal/icon_google";
import { IconOct } from "src/assets/terminal/icon_oct";
import ProviderPanel from "./ProviderPanel";
import { cardBrands, statusLabel, statusTone, type TTone } from "./providersData";

// The "new style" building blocks shared by every Providers tab and dialog. This module only depends on UI
// primitives, so the tabs and dialogs can import it despite importing each other.

export const baseFont = css`
	font: 13px/1.5 "Euclid Circular B", sans-serif;
	letter-spacing: 0;
	color: #344054;
`;

const focusRing = css`
	&:focus-visible {
		outline: 2px solid #84adff;
		outline-offset: 2px;
	}
`;

/* ---------- Tab layout ---------- */

export const TabSurface = styled.div`
	${baseFont};
	min-width: 0;
	padding-top: 0;
	.intro {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 12px;
		margin-bottom: 12px;
	}
	.intro p {
		margin: 0;
		max-width: 720px;
		color: #667085;
	}
	.intro .buttons {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}
`;

export const TabIntro = ({ text, actions }: { text: ReactNode; actions?: ReactNode }) => { useLocale(); return ((
	<div className="intro">
		<p>{text}</p>
		{actions && <div className="buttons">{actions}</div>}
	</div>
)); };

/* ---------- Directory card: toolbar, column head, rows, footer ---------- */

const stackedRows = css`
	.table {
		display: block;
	}
	.head {
		display: none;
	}
	.row {
		display: flex;
		flex-wrap: wrap;
		gap: 6px 18px;
		padding: 12px 16px;
	}
	.row .break {
		display: block;
		flex-basis: 100%;
		height: 0;
		order: 2;
	}
	.cell.main {
		flex: 1 1 0;
		order: 0;
	}
	.cell.trail {
		flex: 0 0 auto;
		order: 1;
	}
	.cell.meta {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		order: 3;
		max-width: 100%;
	}
	.cell.meta:empty {
		display: none;
	}
	.cell.meta[data-label]::before {
		content: attr(data-label) ": ";
		color: #98a2b3;
		font-size: 12px;
	}
`;

const Directory = styled.section`
	${baseFont};
	min-width: 0;
	/* clip, not hidden: the corners stay round and a sticky head can still follow the outer scroller. */
	overflow: clip;
	border: 1px solid #d0d5dd;
	border-radius: 12px;
	background: #fff;
	box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
	/* A padded scroller sets --sticky-head-top to minus its padding, so the head meets its top edge. */
	&.sticky-head .head {
		position: sticky;
		top: var(--sticky-head-top, 0);
		z-index: 2;
	}
	.toolbar {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px 10px;
		padding: 12px 16px;
		border-bottom: 1px solid #eaecf0;
	}
	.toolbar .grow {
		flex: 1 1 auto;
	}
	/* The name column takes the free width; every other column is as wide as its content, so the details gather at
	   the right. Rows share one set of tracks through subgrid, which keeps content-sized columns aligned. */
	.table {
		display: grid;
		grid-template-columns: var(--cols);
		column-gap: 40px;
	}
	.table > * {
		grid-column: 1 / -1;
	}
	.head,
	.row {
		display: grid;
		grid-template-columns: subgrid;
		align-items: center;
		padding: 0 16px;
	}
	.head {
		padding-top: 9px;
		padding-bottom: 9px;
		border-bottom: 1px solid #eaecf0;
		background: #f9fafb;
		color: #667085;
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		white-space: nowrap;
	}
	/* The last column holds right-aligned status and actions, so its head follows them. */
	.head > div:last-child {
		padding-right: 24px;
		text-align: right;
	}
	.row {
		position: relative;
		min-height: 60px;
		padding-top: 10px;
		padding-bottom: 10px;
		border-bottom: 1px solid #eaecf0;
		transition: background-color 0.12s;
	}
	.row:last-child {
		border-bottom: 0;
	}
	.row.clickable {
		cursor: pointer;
	}
	.row.clickable:hover {
		background: #f9fafb;
	}
	.row.highlighted {
		background: #eff8ff;
		box-shadow: inset 3px 0 0 #2e90fa;
	}
	.row.muted .entity-name {
		color: #667085;
	}
	.row:focus-visible {
		outline: 2px solid #84adff;
		outline-offset: -2px;
	}
	.row .break {
		display: none;
	}
	.cell {
		min-width: 0;
	}
	.cell.trail {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 8px;
	}
	.chevron {
		color: #98a2b3;
		transition: transform 0.15s, color 0.15s;
	}
	.row.clickable:hover .chevron {
		color: #475467;
		transform: translateX(2px);
	}
	.foot {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 8px;
		padding: 10px 16px;
		border-top: 1px solid #eaecf0;
		color: #667085;
		font-size: 12px;
	}
	.message {
		padding: 36px 16px;
	}
	/* A narrow card (not the window: the app menu takes part of it) keeps the name and status on one line and
	   lists the other columns under them. Browsers without subgrid get the same stacked rows. */
	container: directory / inline-size;
	@container directory (max-width: 979px) {
		${stackedRows}
	}
	@supports not (grid-template-columns: subgrid) {
		${stackedRows}
	}
`;

export const DirectoryCard = ({
	columns,
	head,
	toolbar,
	footer,
	stickyHead,
	children,
}: {
	columns: string;
	head?: ReactNode[];
	toolbar?: ReactNode;
	footer?: ReactNode;
	/** Keeps the column head visible while the surrounding panel scrolls. */
	stickyHead?: boolean;
	children: ReactNode;
}) => { useLocale(); return ((
	<Directory className={stickyHead ? "sticky-head" : undefined} style={{ "--cols": columns } as CSSProperties}>
		{toolbar && <div className="toolbar">{toolbar}</div>}
		<div className="table" role="table">
			{head && (
				<div className="head" role="row">
					{head.map((label, index) => (
						<div key={index} role="columnheader">
							{label}
						</div>
					))}
				</div>
			)}
			{children}
		</div>
		{footer && <div className="foot">{footer}</div>}
	</Directory>
)); };

export const DirectoryRow = ({
	onOpen,
	label,
	muted,
	highlighted,
	children,
}: {
	onOpen?: () => void;
	label?: string;
	muted?: boolean;
	/** Scrolled into view and marked, for a row a link pointed at. */
	highlighted?: boolean;
	children: ReactNode;
}) => {
	useLocale();
	const row = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (highlighted) row.current?.scrollIntoView({ behavior: "smooth", block: "center" });
	}, [highlighted]);
	const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (!onOpen || event.target !== event.currentTarget) return;
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			onOpen();
		}
	};
	return (
		<div
			ref={row}
			role="row"
			className={`row${onOpen ? " clickable" : ""}${muted ? " muted" : ""}${highlighted ? " highlighted" : ""}`}
			tabIndex={onOpen ? 0 : undefined}
			aria-label={label}
			onClick={onOpen}
			onKeyDown={onKeyDown}>
			{children}
			<span className="break" aria-hidden="true" />
		</div>
	);
};

export const Cell = ({ kind = "meta", label, className, children }: { kind?: "main" | "meta" | "trail"; label?: string; className?: string; children?: ReactNode }) => { useLocale(); return ((
	<div role="cell" className={`cell ${kind}${className ? " " + className : ""}`} data-label={kind === "meta" ? label : undefined}>
		{children}
	</div>
)); };

export const RowChevron = () => { useLocale(); return (<ChevronRight className="chevron" size={16} aria-hidden="true" />); };

/* Stops a click inside a row from also opening the row. */
export const stop = (handler: () => void) => (event: { stopPropagation: () => void }) => {
	event.stopPropagation();
	handler();
};

const Entity = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	min-width: 0;
	.entity-text {
		min-width: 0;
	}
	.entity-name {
		display: flex;
		align-items: center;
		gap: 6px;
		min-width: 0;
		color: #182230;
		font-weight: 600;
	}
	.entity-name > span {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.entity-sub {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 2px 8px;
		margin-top: 1px;
		color: #667085;
		font-size: 12px;
	}
`;

export const EntityCell = ({ icon, name, extra, sub }: { icon?: ReactNode; name: ReactNode; extra?: ReactNode; sub?: ReactNode }) => { useLocale(); return ((
	<Entity>
		{icon}
		<div className="entity-text">
			<div className="entity-name">
				<span className="entity-name-text">{name}</span>
				{extra}
			</div>
			{sub && <div className="entity-sub">{sub}</div>}
		</div>
	</Entity>
)); };

const Tile = styled.span`
	display: inline-grid;
	place-items: center;
	flex: 0 0 34px;
	width: 34px;
	height: 34px;
	border: 1px solid #eaecf0;
	border-radius: 9px;
	background: #f9fafb;
	color: #475467;
	svg {
		width: 18px;
		height: 18px;
	}
	&.small {
		flex-basis: 26px;
		width: 26px;
		height: 26px;
		border-radius: 7px;
	}
	&.small svg {
		width: 15px;
		height: 15px;
	}
`;

export const IconTile = ({ children, small, title }: { children: ReactNode; small?: boolean; title?: string }) => { useLocale(); return ((
	<Tile className={small ? "small" : undefined} title={title} aria-hidden={title ? undefined : true}>
		{children}
	</Tile>
)); };

export const MethodGlyph = ({ type }: { type?: string | null }) => {
	useLocale();
	switch (type) {
		case "payment_card":
			return <IconCard size="18px" />;
		case "apple_pay":
			return <IconApple size="18px" />;
		case "google_pay":
			return <IconGoogle size="18px" />;
		case "open_banking":
			return <IconBank size="18px" />;
		case "oct":
			return <IconOct size="18px" />;
		default:
			return <Wallet size={18} />;
	}
};

export const MethodTile = ({ type, small, title }: { type?: string | null; small?: boolean; title?: string }) => { useLocale(); return ((
	<IconTile small={small} title={title}>
		<MethodGlyph type={type} />
	</IconTile>
)); };

const Brands = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	vertical-align: middle;
	svg {
		display: block;
	}
`;

export const CardBrandIcons = ({ instrument }: { instrument?: string | null }) => {
	useLocale();
	const brands = cardBrands(instrument);
	if (!brands.length) return null;
	return (
		<Brands title={brands.map(brand => (brand === "visa" ? "Visa" : "Mastercard")).join(" · ")}>
			{brands.includes("visa") && <IconVisa width={30} height={16} />}
			{brands.includes("mastercard") && <IconMastercard width={24} height={16} />}
		</Brands>
	);
};

/* ---------- Pills, chips, links ---------- */

const pillTones: Record<TTone, string> = {
	positive: "border-color: #abefc6; background: #ecfdf3; color: #067647;",
	neutral: "border-color: #d0d5dd; background: #f9fafb; color: #475467;",
	info: "border-color: #b2ddff; background: #eff8ff; color: #175cd3;",
	warning: "border-color: #fedf89; background: #fffaeb; color: #b54708;",
	negative: "border-color: #fecdca; background: #fef3f2; color: #b42318;",
};

const PillBox = styled.span<{ $tone: TTone }>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	max-width: 100%;
	padding: 1px 8px;
	border: 1px solid;
	border-radius: 6px;
	font-size: 12px;
	line-height: 20px;
	white-space: nowrap;
	${props => pillTones[props.$tone]};
	&::before {
		content: "";
		flex: 0 0 6px;
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: currentColor;
	}
	&.plain::before {
		content: none;
	}
`;

export const StatusPill = ({ status, label, tone }: { status?: string | null; label?: string; tone?: TTone }) => { useLocale(); return ((
	<PillBox $tone={tone ?? statusTone(status)}>{label ?? statusLabel(status)}</PillBox>
)); };

export const Pill = ({ tone = "neutral", children, dot = false, title }: { tone?: TTone; children: ReactNode; dot?: boolean; title?: string }) => { useLocale(); return ((
	<PillBox $tone={tone} className={dot ? undefined : "plain"} title={title}>
		{children}
	</PillBox>
)); };

export const Chip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 5px;
	max-width: 100%;
	padding: 1px 7px;
	border: 1px solid #d0d5dd;
	border-radius: 6px;
	background: #f9fafb;
	color: #344054;
	font-size: 12px;
	line-height: 20px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	svg {
		flex-shrink: 0;
	}
	.key {
		color: #667085;
	}
	&.linked {
		border-color: #b2ccff;
		background: #eff4ff;
		color: #1849a9;
	}
`;

export const Chips = styled.span`
	display: inline-flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px 6px;
	min-width: 0;
`;

export const Mono = styled.span`
	font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
	font-size: 12px;
	font-variant-numeric: tabular-nums;
	overflow-wrap: anywhere;
`;

export const Muted = styled.span`
	color: #98a2b3;
`;

const LinkButtonBox = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 0;
	border: 0;
	background: none;
	color: #175cd3;
	font: inherit;
	font-weight: 500;
	white-space: nowrap;
	cursor: pointer;
	${focusRing};
	&:hover {
		text-decoration: underline;
		text-underline-offset: 2px;
	}
	&:disabled {
		color: #98a2b3;
		cursor: default;
		text-decoration: none;
	}
`;

export const LinkButton = ({ children, onClick, title, disabled }: { children: ReactNode; onClick: () => void; title?: string; disabled?: boolean }) => { useLocale(); return ((
	<LinkButtonBox type="button" title={title} disabled={disabled} onClick={stop(onClick)}>
		{children}
	</LinkButtonBox>
)); };

const WarningBox = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 5px;
	color: #b54708;
	font-size: 12px;
	white-space: nowrap;
	svg {
		flex-shrink: 0;
	}
`;

export const WarningText = ({ children }: { children: ReactNode }) => { useLocale(); return ((
	<WarningBox>
		<TriangleAlert size={13} aria-hidden="true" />
		{children}
	</WarningBox>
)); };

const CopyBox = styled.button`
	display: inline-grid;
	place-items: center;
	width: 22px;
	height: 22px;
	padding: 0;
	border: 0;
	border-radius: 5px;
	background: transparent;
	color: #98a2b3;
	cursor: pointer;
	vertical-align: middle;
	${focusRing};
	&:hover {
		background: #f2f4f7;
		color: #475467;
	}
	&.done {
		color: #067647;
	}
`;

export const CopyButton = ({ value, label = "Copy" }: { value: string; label?: string }) => {
	useLocale();
	const [done, setDone] = useState(false);
	useEffect(() => {
		if (!done) return;
		const timer = window.setTimeout(() => setDone(false), 1400);
		return () => window.clearTimeout(timer);
	}, [done]);
	return (
		<CopyBox
			type="button"
			className={done ? "done" : undefined}
			title={done ? msg("copied") : label}
			aria-label={label}
			onClick={stop(() => {
				void navigator.clipboard?.writeText(value).then(() => setDone(true), () => {});
			})}>
			{done ? <Check size={13} /> : <Copy size={13} />}
		</CopyBox>
	);
};

/* ---------- Filters ---------- */

const SearchBox = styled.div`
	flex: 0 1 300px;
	min-width: 200px;
	.search-icon {
		display: flex;
		align-items: center;
		padding-left: 11px;
		color: #98a2b3;
	}
	@media (max-width: 600px) {
		flex-basis: 100%;
	}
`;

/** The standard small input with a search glyph. */
export const SearchField = ({ value, onChange, placeholder }: { value: string; onChange: (value: string) => void; placeholder: string }) => {
	 useLocale();
	// The input reads its value from this record, so a new record is made only when the value changes.
	const data = useMemo(() => ({ query: value }), [value]);
	return (
		<SearchBox>
			<Input
				name="query"
				type="search"
				size="small"
				data={data}
				placeholder={placeholder}
				onChange={onChange}
				iconLeft={
					<span className="search-icon" aria-hidden="true">
						<Search size={15} />
					</span>
				}
			/>
		</SearchBox>
	);
};

const Segments = styled.div`
	display: inline-flex;
	flex-wrap: wrap;
	gap: 6px;
	button {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		height: 34px;
		padding: 0 12px;
		border: 1px solid #d0d5dd;
		border-radius: 6px;
		background: #fff;
		color: #475467;
		font: inherit;
		font-size: 12px;
		font-weight: 500;
		white-space: nowrap;
		cursor: pointer;
		transition: background-color 0.12s, border-color 0.12s, color 0.12s;
		${focusRing};
	}
	button:hover {
		background: #f9fafb;
		color: #182230;
	}
	button[aria-pressed="true"] {
		border-color: #b2ccff;
		background: #eff4ff;
		color: #1849a9;
	}
	button.warning[aria-pressed="false"] .count {
		color: #b54708;
	}
	.count {
		color: #98a2b3;
		font-variant-numeric: tabular-nums;
	}
	button[aria-pressed="true"] .count {
		color: #175cd3;
	}
`;

export type TFilterOption<T extends string> = { id: T; label: string; count?: number; warning?: boolean; hidden?: boolean };

export const FilterChips = <T extends string>({ options, value, onChange, label }: { options: TFilterOption<T>[]; value: T; onChange: (value: T) => void; label: string }) => { useLocale(); return ((
	<Segments role="group" aria-label={label}>
		{options
			.filter(option => !option.hidden)
			.map(option => (
				<button key={option.id} type="button" className={option.warning ? "warning" : undefined} aria-pressed={value === option.id} onClick={() => onChange(option.id)}>
					{option.label}
					{option.count !== undefined && <span className="count">{option.count}</span>}
				</button>
			))}
	</Segments>
)); };

const SelectBox = styled.div`
	flex: 0 0 200px;
	min-width: 0;
`;

/** The standard small select; an empty value stands for "all". */
export const FilterSelect = ({
	label,
	value,
	options,
	onChange,
}: {
	label: string;
	value: string;
	options: { value: string; label: string }[];
	onChange: (value: string) => void;
}) => {
	useLocale();
	const values = [{ name: label, value: "" }, ...options.map(option => ({ name: option.label, value: option.value }))];
	return (
		<SelectBox>
			<Select<{ name: string; value: string }>
				size="small"
				values={values}
				value={values.find(option => option.value === value) ?? values[0]}
				onChange={next => onChange(typeof next === "object" && next ? next.value : String(next ?? ""))}
			/>
		</SelectBox>
	);
};

/* ---------- States ---------- */

const EmptyBox = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	padding: 40px 20px;
	text-align: center;
	.empty-icon {
		display: grid;
		place-items: center;
		width: 44px;
		height: 44px;
		margin-bottom: 6px;
		border: 1px solid #eaecf0;
		border-radius: 12px;
		background: #f9fafb;
		color: #667085;
	}
	h4 {
		margin: 0;
		color: #182230;
		font-size: 14px;
		font-weight: 600;
	}
	p {
		max-width: 440px;
		margin: 0;
		color: #667085;
	}
	.empty-action {
		margin-top: 10px;
	}
`;

export const EmptyState = ({ icon, title, text, action }: { icon: ReactNode; title: string; text?: ReactNode; action?: ReactNode }) => { useLocale(); return ((
	<EmptyBox>
		<span className="empty-icon" aria-hidden="true">
			{icon}
		</span>
		<h4>{title}</h4>
		{text && <p>{text}</p>}
		{action && <div className="empty-action">{action}</div>}
	</EmptyBox>
)); };

const SkeletonBox = styled.div`
	div {
		display: flex;
		align-items: center;
		gap: 12px;
		height: 60px;
		padding: 0 16px;
		border-bottom: 1px solid #eaecf0;
	}
	div:last-child {
		border-bottom: 0;
	}
	span {
		display: block;
		height: 10px;
		border-radius: 5px;
		background: linear-gradient(90deg, #f2f4f7 0%, #e4e7ec 50%, #f2f4f7 100%);
		background-size: 200% 100%;
		animation: providers-shimmer 1.2s ease-in-out infinite;
	}
	span.square {
		flex: 0 0 34px;
		height: 34px;
		border-radius: 9px;
	}
	@keyframes providers-shimmer {
		from {
			background-position: 100% 0;
		}
		to {
			background-position: -100% 0;
		}
	}
	@media (prefers-reduced-motion: reduce) {
		span {
			animation: none;
		}
	}
`;

export const SkeletonRows = ({ rows = 3 }: { rows?: number }) => { useLocale(); return ((
	<SkeletonBox role="status" aria-label={msg("loading_dc380888")}>
		{Array.from({ length: rows }, (_, index) => (
			<div key={index}>
				<span className="square" />
				<span style={{ width: `${28 - index * 4}%` }} />
				<span style={{ width: "12%", marginLeft: "auto" }} />
			</div>
		))}
	</SkeletonBox>
)); };

const notices = {
	info: "border-color: #b2ddff; background: #eff8ff; color: #175cd3;",
	warning: "border-color: #fedf89; background: #fffaeb; color: #93370d;",
	error: "border-color: #fecdca; background: #fef3f2; color: #b42318;",
};

const NoticeBox = styled.div<{ $tone: keyof typeof notices }>`
	display: flex;
	align-items: flex-start;
	gap: 10px;
	margin-bottom: 12px;
	padding: 10px 14px;
	border: 1px solid;
	border-radius: 10px;
	font-size: 13px;
	${props => notices[props.$tone]};
	> svg {
		flex-shrink: 0;
		margin-top: 2px;
	}
	.notice-text {
		flex: 1;
		min-width: 0;
		overflow-wrap: anywhere;
	}
	.notice-action {
		flex-shrink: 0;
	}
`;

export const Notice = ({ tone = "info", icon, children, action }: { tone?: keyof typeof notices; icon?: ReactNode; children: ReactNode; action?: ReactNode }) => { useLocale(); return ((
	<NoticeBox className="notice" $tone={tone} role={tone === "error" ? "alert" : "status"}>
		{icon ?? (tone === "info" ? <Info size={16} aria-hidden="true" /> : <TriangleAlert size={16} aria-hidden="true" />)}
		<div className="notice-text">{children}</div>
		{action && <div className="notice-action">{action}</div>}
	</NoticeBox>
)); };

/* ---------- Dialog shell ---------- */

const DialogHeader = styled.header`
	${baseFont};
	flex: 0 0 auto;
	padding: 20px 24px 16px;
	border-bottom: 1px solid #edf0f5;
	background: #fff;
	.top {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 20px;
	}
	.identity {
		flex: 1;
		min-width: 0;
	}
	.eyebrow {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 6px;
		margin-bottom: 5px;
		color: #667085;
		font-size: 11px;
	}
	.title-line {
		display: flex;
		align-items: center;
		gap: 10px;
		min-width: 0;
	}
	h2 {
		min-width: 0;
		margin: 0;
		color: #182230;
		font-size: 21px;
		font-weight: 600;
		line-height: 1.3;
		overflow-wrap: anywhere;
	}
	.actions {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		gap: 6px;
	}
	.meta {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px 14px;
		margin-top: 10px;
		color: #667085;
		font-size: 12px;
	}
	.meta .push {
		margin-left: auto;
		color: #98a2b3;
		font-size: 11px;
	}
	.state {
		display: inline-flex;
		align-items: center;
		gap: 7px;
	}
	.state::before {
		content: "";
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: currentColor;
	}
	.state.positive {
		color: #18774d;
	}
	.state.negative {
		color: #b42318;
	}
	@media (max-width: 600px) {
		padding: 18px;
		.meta .push {
			flex-basis: 100%;
			margin-left: 0;
		}
	}
`;

const IconButtonBox = styled.button`
	display: grid;
	place-items: center;
	flex: 0 0 32px;
	width: 32px;
	height: 32px;
	padding: 0;
	border: 0;
	border-radius: 7px;
	background: transparent;
	color: #667085;
	cursor: pointer;
	${focusRing};
	&:hover:not(:disabled) {
		background: #f2f4f7;
		color: #182230;
	}
	&.accent:hover:not(:disabled) {
		background: #eff4ff;
		color: #175cd3;
	}
	&.danger:hover:not(:disabled) {
		background: #fef3f2;
		color: #b42318;
	}
	&:disabled {
		opacity: 0.5;
		cursor: default;
	}
`;

export const IconButton = ({
	label,
	onClick,
	children,
	tone,
	disabled,
}: {
	label: string;
	onClick: () => void;
	children: ReactNode;
	tone?: "accent" | "danger";
	disabled?: boolean;
}) => { useLocale(); return ((
	<IconButtonBox type="button" className={tone} title={label} aria-label={label} disabled={disabled} onClick={stop(onClick)}>
		{children}
	</IconButtonBox>
)); };

export const StateDot = ({ status, label }: { status?: string | null; label?: string }) => { useLocale(); return ((
	<span className={`state ${statusTone(status)}`}>{label ?? statusLabel(status)}</span>
)); };

/** The new-style popup head: a small context line, the title, optional actions before the close button, and a meta line. */
export const PopupHeader = ({
	eyebrow,
	title,
	titleAction,
	actions,
	meta,
	onClose,
}: {
	eyebrow: ReactNode;
	title: ReactNode;
	titleAction?: ReactNode;
	actions?: ReactNode;
	meta?: ReactNode;
	onClose: () => void;
}) => { useLocale(); return ((
	<DialogHeader>
		<div className="top">
			<div className="identity">
				<div className="eyebrow">{eyebrow}</div>
				<div className="title-line">
					<h2>{title}</h2>
					{titleAction}
				</div>
			</div>
			<div className="actions">
				{actions}
				<IconButton label={msg("close")} onClick={onClose}>
					<X size={18} />
				</IconButton>
			</div>
		</div>
		{meta && <div className="meta">{meta}</div>}
	</DialogHeader>
)); };

/** A dialog in the new style: white header with a small context line, then a body or tabs over a tinted ground. */
export const ProviderDialog = ({
	eyebrow,
	title,
	titleAction,
	meta,
	onClose,
	width = 860,
	fullHeight,
	busy,
	children,
}: {
	eyebrow: ReactNode;
	title: ReactNode;
	titleAction?: ReactNode;
	meta?: ReactNode;
	onClose: () => void;
	width?: number;
	fullHeight?: boolean;
	busy?: boolean;
	children: ReactNode;
}) => {
	useLocale();
	const root = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const previous = document.activeElement;
		root.current?.focus({ preventScroll: true });
		return () => {
			if (previous instanceof HTMLElement && previous.isConnected) previous.focus({ preventScroll: true });
		};
	}, []);
	return (
		<ProviderPanel
			decorator="absolute"
			growContent
			disableScroll
			busy={busy}
			onClose={onClose}
			sx={{
				padding: "0",
				width: width + "px",
				maxWidth: "94vw",
				height: fullHeight ? "88dvh" : undefined,
				maxHeight: "88dvh",
				minHeight: 0,
				borderRadius: "12px",
				overflow: "hidden",
				backgroundColor: "#fff",
			}}>
			<DialogRoot ref={root} role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined} tabIndex={-1}>
				<PopupHeader eyebrow={eyebrow} title={title} titleAction={titleAction} meta={meta} onClose={onClose} />
				{children}
			</DialogRoot>
		</ProviderPanel>
	);
};

const DialogRoot = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	outline: none;
`;

/** Tabs directly under the header; the selected tab's body fills the rest and scrolls on its own. */
export const DialogTabs = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	background: #fff;
	> div:first-child {
		flex: 0 0 auto;
		overflow-x: auto;
		scrollbar-width: none;
	}
`;

export const DialogBody = styled.div<{ $plain?: boolean }>`
	${baseFont};
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
	padding: 20px 24px 24px;
	background: ${props => (props.$plain ? "#fff" : "#f5f7fa")};
	> * + * {
		margin-top: 0;
	}
	@media (max-width: 600px) {
		padding: 16px;
	}
`;

/** Forms sit on the tinted body; their own modern ground and padding would double it. */
export const embeddedFormSx = { padding: 0, borderRadius: 0, backgroundColor: "transparent", maxWidth: "none" } as const;

/* ---------- Cards inside dialogs ---------- */

export const SectionLabel = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin: 24px 0 10px;
	color: #667085;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	&:first-child {
		margin-top: 0;
	}
	.label-text {
		display: inline-flex;
		align-items: center;
		gap: 8px;
	}
	.label-count {
		color: #98a2b3;
	}
	.label-action {
		text-transform: none;
		letter-spacing: 0;
		font-size: 12px;
		font-weight: 500;
	}
`;

export const Section = ({ title, count, action, icon }: { title: ReactNode; count?: number; action?: ReactNode; icon?: ReactNode }) => { useLocale(); return ((
	<SectionLabel>
		<span className="label-text">
			{icon}
			{title}
			{count !== undefined && <span className="label-count">· {count}</span>}
		</span>
		{action && <span className="label-action">{action}</span>}
	</SectionLabel>
)); };

export const Card = styled.section`
	${baseFont};
	min-width: 0;
	overflow: hidden;
	border: 1px solid #d0d5dd;
	border-radius: 10px;
	background: #fff;
	box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
	& + & {
		margin-top: 12px;
	}
	.card-head {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 14px 18px;
	}
	.card-head + .card-body {
		border-top: 1px solid #eaecf0;
	}
	.card-title {
		flex: 1;
		min-width: 0;
		color: #182230;
		font-size: 14px;
		font-weight: 600;
		overflow-wrap: anywhere;
	}
	.card-sub {
		margin-top: 2px;
		color: #667085;
		font-size: 12px;
		font-weight: 400;
	}
	.card-actions {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		gap: 6px;
	}
	.card-body {
		padding: 16px 18px;
	}
	.card-body.flush {
		padding: 0;
	}
`;

export const Facts = styled.dl`
	display: grid;
	grid-template-columns: 160px minmax(0, 1fr);
	align-items: center;
	gap: 8px 16px;
	margin: 0;
	dt {
		color: #667085;
		font-size: 12px;
	}
	dd {
		min-height: 26px;
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 6px 8px;
		min-width: 0;
		margin: 0;
		color: #182230;
		overflow-wrap: anywhere;
	}
	@media (max-width: 600px) {
		grid-template-columns: minmax(0, 1fr);
		gap: 2px;
		dd + dt {
			margin-top: 10px;
		}
	}
`;

/** Clickable rows inside a dialog card: the same rhythm as the page lists, without column heads. */
export const ItemList = styled.div`
	> .item {
		display: flex;
		align-items: center;
		gap: 12px;
		min-height: 56px;
		padding: 10px 18px;
		border-bottom: 1px solid #eaecf0;
		transition: background-color 0.12s;
	}
	> .item:last-child {
		border-bottom: 0;
	}
	> .item.clickable {
		cursor: pointer;
	}
	> .item.clickable:hover {
		background: #f9fafb;
	}
	> .item:focus-visible {
		outline: 2px solid #84adff;
		outline-offset: -2px;
	}
	.item-main {
		flex: 1 1 auto;
		min-width: 0;
	}
	.item-side {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		flex-wrap: wrap;
		justify-content: flex-end;
		gap: 6px 10px;
	}
	.item.clickable:hover .chevron {
		color: #475467;
		transform: translateX(2px);
	}
	.chevron {
		flex-shrink: 0;
		color: #98a2b3;
		transition: transform 0.15s, color 0.15s;
	}
	@media (max-width: 600px) {
		> .item {
			flex-wrap: wrap;
		}
		.item-side {
			justify-content: flex-start;
		}
	}
`;

export const Item = ({ onOpen, label, children }: { onOpen?: () => void; label?: string; children: ReactNode }) => { useLocale(); return ((
	<div
		className={`item${onOpen ? " clickable" : ""}`}
		role={onOpen ? "button" : undefined}
		tabIndex={onOpen ? 0 : undefined}
		aria-label={label}
		onClick={onOpen}
		onKeyDown={event => {
			if (!onOpen || event.target !== event.currentTarget) return;
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				onOpen();
			}
		}}>
		{children}
	</div>
)); };
